import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import styled from 'styled-components';

import { memo, useContext } from 'react';
import MultiRecord from './MultiRecord';
import TraitsRecordCardHeader from './TraitsRecordCardHeader';
import { ProcedureInterface } from '../../types';
import UnmappedRecord from '../../types/UnmappedRecord';
import TraitsRecordCardType from '../../enums/TraitsRecordCardType';
import TraitsReconciliationContext from '../../context/TraitsReconciliationContext';
import TraitsRecordCardActions from './TraitRecordCardActions';

const StyledCard = styled(Card)`
  && {
    border-radius: 8px;
  }
`;

const StyledCardContent = styled(CardContent)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px !important;
`;

interface TraitsRecordCardProps {
  currentProcedureConfig: ProcedureInterface;
  unmappedRecord: UnmappedRecord;
  onAddNew: (unmappedRecordId: string, subjectId: string, traitsStudyProcedureKey: number) => Promise<void>;
  onMap: (unmappedRecordId: string, subjectId: string, traitsStudyProcedureKey: number, patientRecordUid: string, isDeleted: boolean) => Promise<void>;
  onDismiss: (unmappedRecordId: string) => Promise<void>;
}

const TraitsRecordCard = memo(({
  currentProcedureConfig: procedureConfig, unmappedRecord, onAddNew, onMap, onDismiss,
}: Readonly<TraitsRecordCardProps>) => {
  const { patientRecordSummaries, currentStudy } = useContext(TraitsReconciliationContext);
  // Gets the TraitsRecordCardType based on the is_active status of the first data point, falls back to true if undefined.
  // If is_active is true, the datapoint was an update. If is_active is false, the datapoint was deleted.
  const traitsRecordCardType = (unmappedRecord?.dataPoints[0]?.is_active ?? true) ? TraitsRecordCardType.UPDATE : TraitsRecordCardType.DELETED;

  const handleAddNewClick = () => {
    onAddNew(unmappedRecord.recordId, unmappedRecord.dataPoints[0].subject_id, unmappedRecord.traitsStudyProcedureKey);
  };

  const handleMapClick = (patientRecordUid: string, isDeleted: boolean) => {
    onMap(unmappedRecord.recordId, unmappedRecord.dataPoints[0].subject_id, unmappedRecord.traitsStudyProcedureKey, patientRecordUid, isDeleted);
  };

  const handleDismissClick = () => {
    onDismiss(unmappedRecord.recordId);
  };

  return (
    <StyledCard variant="outlined" sx={{ marginBottom: 2 }} data-testid={`record-card-${unmappedRecord.recordId}`}>
      <StyledCardContent>
        {/* TODO: Figure out right spot to derive the protocol number */}
        <TraitsRecordCardHeader
          protocolNumber={currentStudy?.protocolNumber ?? 'N/A'}
          procedureName={procedureConfig.name}
          traitsRecordCardType={traitsRecordCardType}
        />
        <MultiRecord procedureConfig={procedureConfig} unmappedRecord={unmappedRecord} />
        <TraitsRecordCardActions
          traitsRecordCardType={traitsRecordCardType}
          patientRecordSummaries={patientRecordSummaries}
          recordId={unmappedRecord.recordId}
          onAddNewClick={handleAddNewClick}
          onMapClick={handleMapClick}
          onDismissClick={handleDismissClick}
        />
      </StyledCardContent>
    </StyledCard>
  );
});
TraitsRecordCard.displayName = 'TraitsRecordCard';

export default TraitsRecordCard;
